import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { chunkedUpload, uploadErrorMessage, uploadStatus } from "@/lib/chunkedUpload";
import { fmtBytes } from "@/components/project/UploadProgress";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { undoToast } from "@/lib/undo";
import { UploadCloud, ScanLine, AlertOctagon, FileBarChart2, ShieldCheck, Trash2 } from "lucide-react";
import { DocumentsTable } from "@/components/project/DocumentsTable";
import { ReviewHistory } from "@/components/project/ReviewHistory";
import { DeleteConfirmDialog, UploadDialog, PermissionsDialog, EMPTY_UPLOAD } from "@/components/project/ProjectDialogs";

const DOC_POLL_MS = 4000;
const btn = "border border-slate-700 hover:border-amber-500 text-slate-100 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2";

function Stat({ label, value, accent = "text-slate-100" }) {
  return <div className="sc-card p-4"><div className="label-cap">{label}</div><div className={`font-heading text-2xl font-extrabold mt-2 ${accent}`}>{value}</div></div>;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, activeWorkspaceId, currentRole } = useAuth();
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [findings, setFindings] = useState([]);
  const [open, setOpen] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, id, label}
  const [uploadMode, setUploadMode] = useState("file");
  const [form, setForm] = useState(EMPTY_UPLOAD);
  const [uploading, setUploading] = useState(false);
  const [up, setUp] = useState(null); // {phase: transfer|finalizing|processing|error, name, loaded, total, rate, elapsed, docId, error, sid, session, saved, resumable}
  const [limits, setLimits] = useState(null);
  useEffect(() => { api.get(`/uploads/limits`).then((r) => setLimits(r.data)).catch(() => {}); }, []);
  const inFlight = useRef(false);
  const upRef = useRef(null);
  useEffect(() => { upRef.current = up; }, [up]);
  const resetUpload = useCallback(() => { if (!inFlight.current) setUp(null); }, []);
  useEffect(() => {
    if (up?.phase !== "transfer" && up?.phase !== "finalizing") return;
    const warn = (e) => { e.preventDefault(); e.returnValue = "Your file is still uploading — leaving now will interrupt it."; return e.returnValue; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [up?.phase]);
  const upDoc = up?.docId ? docs.find((d) => d.id === up.docId) : null;
  const [permOpen, setPermOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [projPerms, setProjPerms] = useState([]);
  const [permForm, setPermForm] = useState({ user_id: "", role: "reviewer" });

  const load = useCallback(async () => {
    const [p, d, r, f] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/documents`), api.get(`/projects/${id}/reviews`), api.get(`/projects/${id}/findings`)]);
    setProject(p.data); setDocs(d.data); setRuns(r.data); setFindings(f.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const processing = docs.some((d) => d.status === "processing" || d.job_active);
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => api.get(`/projects/${id}/documents`).then((r) => { setDocs(r.data); if (!r.data.some((d) => d.status === "processing" || d.job_active)) { toast.success("Document processing finished"); load(); } }), DOC_POLL_MS);
    return () => clearInterval(t);
  }, [processing, id, load]);
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") api.get(`/projects/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {}); };
    window.addEventListener("focus", onFocus); document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [id]);

  const retryDoc = useCallback(async (d) => {
    try { await api.post(`/documents/${d.id}/retry`); toast.info("Reprocessing from the stored original — no re-upload needed."); load(); }
    catch (err) { toast.error(apiError(err, "Could not retry processing")); }
  }, [load]);

  const loadPerms = useCallback(async () => {
    const [m, p] = await Promise.all([api.get(`/workspaces/${activeWorkspaceId}/members`), api.get(`/projects/${id}/permissions`)]);
    setMembers(m.data); setProjPerms(p.data);
  }, [activeWorkspaceId, id]);
  useEffect(() => { if (permOpen && activeWorkspaceId) loadPerms(); }, [permOpen, activeWorkspaceId, loadPerms]);

  const phaseRef = useRef(null); // live upload phase (state `up` is a stale closure inside submit)
  const submit = async () => {
    if (inFlight.current) return; // repeated clicks can never start a second upload
    if (uploadMode === "file" && !form.file) return toast.error("Choose a PDF or text file");
    inFlight.current = true;
    setUploading(true);
    let errored = false;
    try {
      if (uploadMode === "file") {
        const name = form.name || form.file.name, isPdf = form.file.name.toLowerCase().endsWith(".pdf");
        if (limits && form.file.size > limits.max_bytes) {
          errored = true;
          setUp({ phase: "error", name, total: form.file.size, error: `This file is ${fmtBytes(form.file.size)}; the maximum upload size is ${limits.max_mb} MB.`, saved: "none" });
          return;
        }
        if (isPdf) {
          const prev = upRef.current;
          const session = prev?.phase === "error" && prev.session && prev.file === form.file ? prev.session : null;
          phaseRef.current = "transfer";
          try {
            const doc = await chunkedUpload({ file: form.file, projectId: id, form, limits, session, onState: (st) => { phaseRef.current = st.phase; setUp((s) => ({ ...(s || {}), name, file: form.file, ...st })); } });
            setUp((s) => ({ ...s, phase: "processing", loaded: s.total, docId: doc.id, already: !!doc.already_completed }));
            if (doc.already_completed) { load(); } else setDocs((ds) => [doc, ...ds.filter((d) => d.id !== doc.id)]);
          } catch (e) {
            errored = true;
            // Only claim "nothing was saved" when that is provable: the document is created solely inside /complete.
            const status = e?.response?.status;
            const duringComplete = phaseRef.current === "finalizing";
            const saved = duringComplete && (!e.response || status >= 500) ? "uncertain" : "none";
            setUp((s) => ({ ...(s || { name, total: form.file.size }), phase: "error", error: uploadErrorMessage(e, limits), saved, file: form.file, resumable: !!s?.sid && status !== 413 && status !== 422 }));
            if (!prev) toast.error(uploadErrorMessage(e, limits));
          }
        } else {
          const fd = new FormData();
          fd.append("project_id", id); fd.append("doc_type", form.doc_type); fd.append("name", name); fd.append("csi_division", form.csi_division); fd.append("file", form.file);
          setUp({ phase: "transfer", name, loaded: 0, total: form.file.size, rate: 0, elapsed: 0 });
          const { data: doc } = await api.post(`/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
          setUp((s) => ({ ...s, phase: "processing", loaded: s.total, docId: doc.id }));
          setDocs((ds) => [doc, ...ds.filter((d) => d.id !== doc.id)]);
        }
        if (!errored) setForm(EMPTY_UPLOAD); // keep the file selected after an error so Try again / resume can reuse it
      } else {
        await api.post(`/documents`, { project_id: id, name: form.name || "Pasted spec", doc_type: form.doc_type, csi_division: form.csi_division, content: form.content });
        toast.success("Document uploaded");
        setOpen(false); setForm(EMPTY_UPLOAD); load();
      }
    } catch (e) {
      const msg = uploadErrorMessage(e, limits);
      setUp((s) => (s ? { ...s, phase: "error", error: msg, saved: "none" } : null));
      if (!upRef.current) toast.error(msg);
    } finally { inFlight.current = false; setUploading(false); phaseRef.current = null; }
  };

  const verifyUpload = useCallback(async () => {
    if (!up?.sid) return;
    try {
      const s = await uploadStatus(up.sid);
      if (s.status === "completed") { toast.success("The upload had completed — document registered."); setUp((u) => ({ ...u, phase: "processing", docId: s.document_id })); load(); }
      else { toast.info(`No document was registered (upload session is ${s.status}). You can safely try again.`); setUp((u) => ({ ...u, saved: "none", resumable: s.status === "open" })); }
    } catch (e) { toast.error(apiError(e, "Could not verify upload status")); }
  }, [up?.sid, load]);

  const setPerm = async () => {
    if (!permForm.user_id) return toast.error("Select a user");
    const fd = new FormData(); fd.append("user_id", permForm.user_id); fd.append("role", permForm.role);
    await api.post(`/projects/${id}/permissions`, fd);
    toast.success("Project role set"); loadPerms();
  };
  const rmPerm = async (ppid) => { await api.delete(`/projects/${id}/permissions/${ppid}`); toast.success("Override removed"); loadPerms(); };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "document") {
        const { data } = await api.delete(`/documents/${confirm.id}`);
        undoToast(`Deleted ${confirm.label}`, data.undo_id, () => load());
      } else if (confirm.type === "run") { await api.delete(`/reviews/${confirm.id}`); toast.success("Deleted"); }
      else if (confirm.type === "project") {
        const { data } = await api.delete(`/projects/${id}`);
        navigate("/projects");
        undoToast(`Project deleted — ${project?.name || ""}`.trim(), data.undo_id, () => navigate(`/projects/${id}`));
        return;
      }
      setConfirm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!project) return <div className="p-10 text-slate-400">Loading…</div>;
  const critCount = findings.filter((f) => f.severity === "critical").length;

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="project-detail-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap text-amber-500">// PROJECT</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1">{project.name}</h1>
          <div className="text-sm text-slate-500 font-mono mt-1">{project.number || "—"} · {project.tonnage || "—"} · {project.location || "—"}</div>
        </div>
        <div className="flex gap-2">
          <Link to={`/projects/${id}/review`} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="go-to-review-button"><ScanLine className="w-4 h-4" /> Run AI Review</Link>
          <Link to={`/projects/${id}/findings`} className={btn} data-testid="go-to-findings-button"><AlertOctagon className="w-4 h-4" /> Findings ({findings.length})</Link>
          <Link to={`/projects/${id}/report`} className={btn} data-testid="go-to-report-button"><FileBarChart2 className="w-4 h-4" /> PM Report</Link>
          {currentRole === "admin" && (<>
            <button onClick={() => setPermOpen(true)} className={btn} data-testid="project-permissions-button"><ShieldCheck className="w-4 h-4" /> Permissions</button>
            <button onClick={() => setConfirm({ type: "project", id, label: `project "${project.name}" and ALL its documents, reviews and findings` })} className="border border-red-900/60 hover:border-red-500 text-red-400 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="delete-project-button"><Trash2 className="w-4 h-4" /></button>
          </>)}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <Stat label="Documents" value={docs.length} /><Stat label="Total findings" value={findings.length} /><Stat label="Critical" value={critCount} accent="text-red-400" /><Stat label="Reviews Run" value={runs.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="sc-card p-6 lg:col-span-8">
          <div className="flex items-center justify-between mb-3">
            <div className="label-cap">Project Documents</div>
            {can("create") && <button onClick={() => setOpen(true)} className="text-xs bg-amber-500/10 border border-amber-500/40 text-amber-400 hover:bg-amber-500/20 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5" data-testid="upload-document-button"><UploadCloud className="w-3.5 h-3.5" /> Upload document</button>}
          </div>
          <DocumentsTable docs={docs} can={can} expandedDoc={expandedDoc} setExpandedDoc={setExpandedDoc} onDelete={setConfirm} onRetry={retryDoc} />
        </div>
        <ReviewHistory runs={runs} can={can} onDelete={setConfirm} />
      </div>

      <DeleteConfirmDialog confirm={confirm} setConfirm={setConfirm} doDelete={doDelete} />
      <UploadDialog open={open} setOpen={setOpen} form={form} setForm={setForm} uploadMode={uploadMode} setUploadMode={setUploadMode} uploading={uploading} submit={submit} up={up} upDoc={upDoc} resetUpload={resetUpload} limits={limits} onVerify={verifyUpload} />
      <PermissionsDialog open={permOpen} setOpen={setPermOpen} projectName={project.name} members={members} projPerms={projPerms} permForm={permForm} setPermForm={setPermForm} setPerm={setPerm} rmPerm={rmPerm} />
    </div>
  );
}
